import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { api } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { TEMPLATE_CATEGORIES, applyTemplate } from '../lib/templateUtils';
import { Loader2, FileText, X, Sparkles } from 'lucide-react';

// Score each template against the supplier context to suggest the most relevant one.
// Higher score = more relevant. Returns null if context insufficient.
const suggestBest = (templates, ctx) => {
  if (!templates || templates.length === 0 || !ctx || !ctx.supplier_name) return null;
  const moqOver = ctx.moq && ctx.quantity ? ctx.moq > ctx.quantity : false;
  const fastShipNeeded = ctx.lead_time_days && ctx.lead_time_days <= 14;
  const tightBudget = ctx.budget && ctx.quantity
    ? (ctx.budget / ctx.quantity) <= 5  // tight: < $5/unit budget
    : false;
  const smallOrder = ctx.quantity && ctx.quantity <= 200;

  const targetCategory = (() => {
    if (moqOver) return 'moq_negotiation';
    if (smallOrder) return 'sample_request';
    if (tightBudget) return 'price_inquiry';
    if (fastShipNeeded) return 'shipping_inquiry';
    return 'price_inquiry';
  })();

  const scored = templates.map((tpl) => {
    let score = 0;
    if (tpl.category === targetCategory) score += 100;
    score += Math.min(tpl.usage_count || 0, 20); // popularity nudge, capped
    return { tpl, score };
  }).sort((a, b) => b.score - a.score);

  return scored[0]?.score > 0 ? scored[0].tpl : null;
};

const TemplatePicker = ({ open, onOpenChange, supplierContext, onApply }) => {
  const { t } = useLanguage();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    api.get('/api/templates')
      .then(({ data }) => { if (!cancelled) setTemplates(data); })
      .catch(() => { if (!cancelled) setTemplates([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  const filtered = activeCategory === 'all'
    ? templates
    : templates.filter((t) => t.category === activeCategory);

  const handlePick = async (tpl) => {
    const adapted = applyTemplate(tpl.body, supplierContext);
    // Fire-and-forget usage tracking
    api.post(`/api/templates/${tpl.id}/use`).catch(() => {});
    onApply({ template: tpl, body: adapted });
    onOpenChange(false);
  };

  const handleSuggest = () => {
    const best = suggestBest(templates, supplierContext);
    if (best) handlePick(best);
  };

  const suggestedExists = Boolean(suggestBest(templates, supplierContext));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg p-0 gap-0 h-screen max-h-screen w-screen rounded-none inset-0 translate-x-0 translate-y-0 sm:inset-auto sm:left-[50%] sm:top-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:h-auto sm:max-h-[85vh] sm:w-auto sm:rounded-lg overflow-hidden flex flex-col"
        data-testid="template-picker"
      >
        <DialogHeader className="px-4 py-3 sm:px-6 sm:py-4 border-b border-slate-200 flex flex-row items-center justify-between space-y-0">
          <div>
            <DialogTitle className="text-base sm:text-lg flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              {t('pickATemplate')}
            </DialogTitle>
            <DialogDescription className="sr-only">{t('pickATemplate')}</DialogDescription>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="sm:hidden p-2 -mr-2 rounded-md text-slate-500 hover:bg-slate-100"
            aria-label="Close"
            data-testid="template-picker-close"
          >
            <X className="w-5 h-5" />
          </button>
        </DialogHeader>

        {/* Category chips */}
        <div className="px-4 sm:px-6 py-3 border-b border-slate-100 flex gap-1.5 overflow-x-auto" data-testid="template-categories">
          <button
            type="button"
            onClick={() => setActiveCategory('all')}
            className={`shrink-0 text-xs px-3 py-1.5 rounded-full border transition-colors ${
              activeCategory === 'all'
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
            }`}
            data-testid="template-category-all"
          >
            {t('allCategories')}
          </button>
          {TEMPLATE_CATEGORIES.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setActiveCategory(cat)}
              className={`shrink-0 text-xs px-3 py-1.5 rounded-full border transition-colors ${
                activeCategory === cat
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
              data-testid={`template-category-${cat}`}
            >
              {t(`cat_${cat}`)}
            </button>
          ))}
        </div>

        {/* Suggest button — surfaces the most relevant template for current context */}
        {suggestedExists && (
          <div className="px-4 sm:px-6 pt-3">
            <Button
              type="button"
              onClick={handleSuggest}
              variant="outline"
              className="w-full justify-start border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-700"
              data-testid="suggest-template-button"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {t('suggestTemplate')}
            </Button>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-3" data-testid="template-picker-body">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-12">{t('noTemplates')}</p>
          )}
          {!loading && filtered.length > 0 && (
            <ul className="space-y-2" data-testid="template-list">
              {filtered.map((tpl) => (
                <li key={tpl.id}>
                  <button
                    type="button"
                    onClick={() => handlePick(tpl)}
                    className="w-full text-left p-3 border border-slate-200 rounded-lg hover:border-blue-300 hover:bg-blue-50/50 transition-colors"
                    data-testid="template-item"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-slate-900 text-sm truncate">{tpl.name}</span>
                      <span className="text-[10px] uppercase tracking-wide bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                        {t(`cat_${tpl.category}`)}
                      </span>
                      {tpl.is_default && (
                        <span className="text-[10px] uppercase bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
                          {t('isDefault')}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-600 line-clamp-2 whitespace-pre-line">{tpl.body}</p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TemplatePicker;
